import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, ListPageLayout, type DataTableColumn } from '@/shared/components/entities'
import { ConfirmDialog, ContextIcon } from '@/shared/components/ui'
import { OverflowMenu, type OverflowMenuItem } from '@/shared/components/ui/OverflowMenu'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { listAccountingClients } from '@/modules/accounting/clients/accountingClientsService'
import { listDocumentBatches, type StoredDocument } from '@/modules/documents'
import {
  BUILT_IN_FISCAL_LAYOUT_TYPES,
  cloneLayoutDefinition,
  createLayoutDefinitionSkeleton,
  deactivateFileLayout,
  definitionFromFileLayout,
  formatLayoutDefinition,
  getLayoutDocumentType,
  getStoredDocumentType,
  listFileLayouts,
  type DeclarativeFileLayout,
  type FileLayoutWithVersion,
  type FiscalDocumentType,
} from '@/modules/file-reader'
import { FileLayoutJsonModal, type FileLayoutExampleOption } from '@/modules/file-reader/FileLayoutJsonModal'

interface AvailableExample {
  partyId: string
  clientName: string
  document: StoredDocument
}

interface LayoutRow {
  id: string
  name: string
  type: FiscalDocumentType
  builtIn: boolean
  layout: FileLayoutWithVersion | null
}

interface EditorState {
  layoutId: string | null
  type: FiscalDocumentType | null
  definition: DeclarativeFileLayout
  initialTab: 'json' | 'ai' | 'test'
}

function downloadDefinition(definition: DeclarativeFileLayout) {
  const url = URL.createObjectURL(new Blob([formatLayoutDefinition(definition)], { type: 'application/json' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${definition.key.replace(/[^a-zA-Z0-9_-]+/g, '_') || 'leiaute'}.json`
  link.click()
  URL.revokeObjectURL(url)
}

export function FileLayoutsPage() {
  const { currentUnit } = useUnit()
  const { t, locale } = useTranslation()
  const navigate = useNavigate()
  const [layouts, setLayouts] = useState<FileLayoutWithVersion[]>([])
  const [examples, setExamples] = useState<AvailableExample[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [deactivating, setDeactivating] = useState<LayoutRow | null>(null)

  const load = useCallback(async () => {
    if (!currentUnit?.id) return
    setLoading(true)
    setError(null)
    try {
      const [savedLayouts, clients] = await Promise.all([
        listFileLayouts(currentUnit.id),
        listAccountingClients(currentUnit.id),
      ])
      const clientBatches = await Promise.all(clients.map(async (client) => ({
        client,
        batches: await listDocumentBatches(client.client_unit_id),
      })))
      const available = clientBatches.flatMap(({ client, batches }) => batches.flatMap((batch) => (batch.documents || [])
        .filter((document) => document.storage_path)
        .map((document) => ({ partyId: client.party_id, clientName: client.name, document }))))
        .sort((left, right) => right.document.created_at.localeCompare(left.document.created_at))
      setLayouts(savedLayouts)
      setExamples(available)
    } catch (loadError) {
      console.error('[FileLayoutsPage] Error loading layouts:', loadError)
      setError(t('accounting.layouts.errors.load'))
    } finally {
      setLoading(false)
    }
  }, [currentUnit?.id, t])

  useEffect(() => { void load() }, [load])

  const typeName = useCallback(
    (type: FiscalDocumentType) => t(`accounting.layoutDocumentTypes.${type}`),
    [t],
  )
  const presetName = useCallback(
    (type: FiscalDocumentType) => t(`accounting.layoutPresetNames.${type}`),
    [t],
  )

  const rows = useMemo<LayoutRow[]>(() => {
    const usedIds = new Set<string>()
    const builtIns = BUILT_IN_FISCAL_LAYOUT_TYPES.map((type) => {
      const saved = layouts.find((layout) => getLayoutDocumentType(layout) === type
        && layout.latest_version?.parser_config?.preset_key === type) || null
      if (saved) usedIds.add(saved.id)
      return {
        id: saved?.id || `builtin:${type}`,
        name: saved?.name || presetName(type),
        type,
        builtIn: !saved,
        layout: saved,
      }
    })
    const custom = layouts.filter((layout) => !usedIds.has(layout.id)).map((layout) => ({
      id: layout.id,
      name: layout.name,
      type: getLayoutDocumentType(layout),
      builtIn: false,
      layout,
    }))
    return [...builtIns, ...custom]
  }, [layouts, presetName])

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    return query
      ? rows.filter((row) => `${row.name} ${typeName(row.type)}`.toLocaleLowerCase(locale).includes(query))
      : rows
  }, [locale, rows, searchQuery, typeName])

  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(value))
  const examplesForType = useCallback((type: FiscalDocumentType | null) => examples.filter((example) => (
    !type || getStoredDocumentType(example.document) === type
  )), [examples])
  const editorExamples = useMemo<FileLayoutExampleOption[]>(() => examplesForType(editor?.type || null).map((example) => ({
    id: example.document.id,
    documentId: example.document.id,
    partyId: example.partyId,
    label: `${typeName(getStoredDocumentType(example.document))} · ${example.document.original_name} · ${example.clientName} · ${formatDate(example.document.created_at)}`,
  })), [editor?.type, examplesForType, locale, typeName])

  const definitionForRow = (row: LayoutRow) => row.layout
    ? definitionFromFileLayout(row.layout)
    : createLayoutDefinitionSkeleton({ type: row.type, name: row.name })

  const openRow = (row: LayoutRow, initialTab: EditorState['initialTab'] = 'json') => {
    setEditor({ layoutId: row.layout?.id || null, type: row.type, definition: definitionForRow(row), initialTab })
    setError(null)
    setSuccess(null)
  }

  const openNew = (initialTab: EditorState['initialTab'] = 'json') => {
    setEditor({ layoutId: null, type: null, definition: createLayoutDefinitionSkeleton(), initialTab })
    setError(null)
    setSuccess(null)
  }

  const duplicateRow = (row: LayoutRow) => setEditor({
    layoutId: null,
    type: row.type,
    definition: cloneLayoutDefinition(definitionForRow(row), t('accounting.layouts.editor.copyName', { name: row.name })),
    initialTab: 'json',
  })

  const testLayout = (example: FileLayoutExampleOption, layoutId: string | null) => {
    const query = layoutId ? `?layout=${encodeURIComponent(layoutId)}` : ''
    navigate(`/accounting/clients/${example.partyId}/documents/${example.documentId}/reader${query}`)
  }

  const deactivate = async () => {
    if (!currentUnit?.id || !deactivating?.layout) return
    try {
      await deactivateFileLayout(currentUnit.id, deactivating.layout.id)
      setSuccess(t('accounting.layouts.deactivated'))
      await load()
    } catch (deactivateError) {
      console.error('[FileLayoutsPage] Error deactivating layout:', deactivateError)
      setError(t('accounting.layouts.errors.deactivate'))
    }
  }

  const statusBadge = (row: LayoutRow) => <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.builtIn ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>{row.builtIn ? t('accounting.layouts.systemPreset') : t('accounting.layouts.configured')}</span>
  const directionForRow = (row: LayoutRow) => row.layout ? definitionFromFileLayout(row.layout).direction : 'import'
  const rowActions = (row: LayoutRow): OverflowMenuItem[] => [
    { label: t('accounting.layouts.actions.editJson'), onClick: () => openRow(row, 'json') },
    { label: t('accounting.layouts.actions.aiInstructions'), onClick: () => openRow(row, 'ai') },
    { label: t('accounting.layouts.actions.test'), onClick: () => openRow(row, 'test') },
    { label: t('accounting.layouts.actions.exportJson'), onClick: () => downloadDefinition(definitionForRow(row)) },
    { label: t('accounting.layouts.actions.duplicate'), onClick: () => duplicateRow(row) },
    ...(row.layout ? [{ label: t('accounting.layouts.actions.deactivate'), danger: true, onClick: () => setDeactivating(row) }] : []),
  ]
  const columns: DataTableColumn<LayoutRow>[] = [
    { key: 'name', label: t('accounting.layouts.columns.name'), render: (row) => <p className="font-semibold text-slate-900 dark:text-white">{row.name}</p> },
    { key: 'type', label: t('accounting.layouts.columns.type'), render: (row) => <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{typeName(row.type)}</span> },
    { key: 'direction', label: t('accounting.layouts.columns.direction'), render: (row) => t(`accounting.layouts.directions.${directionForRow(row)}`) },
    { key: 'status', label: t('accounting.layouts.columns.status'), render: statusBadge },
    { key: 'version', label: t('accounting.layouts.columns.version'), render: (row) => row.layout ? `v${row.layout.latest_version?.version || 1}` : '—' },
    { key: 'fields', label: t('accounting.layouts.columns.fields'), render: (row) => row.layout ? `${row.layout.fields.length} / ${row.layout.mappings.length}` : t('accounting.layouts.preconfigured') },
    { key: 'actions', label: t('accounting.layouts.columns.actions'), align: 'right', render: (row) => <div className="flex justify-end" onClick={(event) => event.stopPropagation()}><OverflowMenu items={rowActions(row)} /></div> },
  ]

  return (
    <>
      <ListPageLayout
        title={t('accounting.layouts.title')}
        description={t('accounting.layouts.subtitle')}
        icon={<ContextIcon context="accounting" className="h-7 w-7 text-emerald-700 dark:text-emerald-400" />}
        onNew={() => openNew('json')}
        newButtonLabel={t('accounting.layouts.newLayout')}
        headerActions={<button type="button" onClick={() => openNew('json')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">{t('accounting.layouts.importJson')}</button>}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('accounting.layouts.search')}
        clearSearchLabel={t('actions.filter')}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onMobileViewOverride={setViewMode}
        showViewToggle
        viewCardLabel={t('accounting.clients.views.cards')}
        viewTableLabel={t('accounting.clients.views.table')}
        loading={loading}
        error={error}
        success={success}
        stickyToolbar={!loading ? <p className="px-1 text-sm text-slate-600 dark:text-slate-400">{t('accounting.layouts.showing', { shown: String(filteredRows.length), total: String(rows.length) })}</p> : undefined}
      >
        {viewMode === 'table'
          ? <DataTable data={filteredRows} columns={columns} keyExtractor={(row) => row.id} onRowClick={(row) => openRow(row)} />
          : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{filteredRows.map((row) => <article key={row.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"><div className="flex items-start justify-between gap-3 px-5 pt-5"><span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{typeName(row.type)}</span><div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>{statusBadge(row)}<OverflowMenu items={rowActions(row)} /></div></div><button type="button" onClick={() => openRow(row)} className="block w-full px-5 pb-5 text-left"><h2 className="mt-4 truncate font-semibold text-slate-900 dark:text-white">{row.name}</h2><p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{row.layout ? t('accounting.layouts.layoutSummary', { version: String(row.layout.latest_version?.version || 1), fields: String(row.layout.fields.length), mappings: String(row.layout.mappings.length) }) : t('accounting.layouts.systemPresetHint')}</p></button><div className="border-t border-slate-100 px-5 py-3 text-xs font-medium text-slate-500 dark:border-slate-800 dark:text-slate-400">{t(`accounting.layouts.directions.${directionForRow(row)}`)}</div></article>)}</div>}
      </ListPageLayout>

      {editor && currentUnit?.id && <FileLayoutJsonModal
        isOpen
        unitId={currentUnit.id}
        layoutId={editor.layoutId}
        initialDefinition={editor.definition}
        initialTab={editor.initialTab}
        examples={editorExamples}
        onClose={() => setEditor(null)}
        onSaved={(saved) => {
          setSuccess(t('accounting.layouts.saved', { version: String(saved.version) }))
          void load()
        }}
        onTest={testLayout}
      />}

      <ConfirmDialog
        isOpen={Boolean(deactivating)}
        onClose={() => setDeactivating(null)}
        onConfirm={() => void deactivate()}
        title={t('accounting.layouts.deactivateTitle')}
        message={t('accounting.layouts.deactivateMessage', { name: deactivating?.name || '' })}
        confirmText={t('accounting.layouts.actions.deactivate')}
        cancelText={t('actions.cancel')}
        type="danger"
      />
    </>
  )
}
